<template>
    <button @click="show = true" class="btn btn-sm btn-info mr-1 mb-1">
      <font-awesome-icon icon="heart"/>&nbsp;{{ $t('title') }}
      <b-modal v-model="show" :title="$t('title')" @close="close" no-close-on-esc no-close-on-backdrop no-enforce-focus>
        <div class="form-group">
          <label><b>{{ $t('downloadTemplate') }}</b></label>
          <div>
            <button @click="downloadTemplate" class="btn btn-sm btn-secondary mr-1 mb-2">
              <i class="fa fa-fw fa-lg fa-file-excel-o"></i>mass-care-template.xlsx
            </button>
          </div>
        </div>
        <div class="form-group">
          <label><b>{{ $t('selectFile') }}</b></label>
          <b-form-file v-model="file"
                       :placeholder="$t('formFilePlaceholder')"
                       :drop-placeholder="$t('formFilePlaceholder')"
                       :browse-text="$t('selectFile')"
                       :disabled="reading||totalRows>0"
                       accept="application/x-iwork-keynote-sffnumbers,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          ></b-form-file>
          <span v-if="fileError" class="small text-danger">{{fileError}}</span>
        </div>
        <div class="form-group">
          <label><b>{{ $t('account') }}</b></label>
          <admin-user-select :model="formModel" attribute="account" :place-holder="$t('selectAccount')" :errors="formErrors"></admin-user-select>
        </div>
        <div v-if="importing || reading" class="alert alert-warning">
          <b-spinner small variant="primary"></b-spinner>&nbsp;
          <span>{{ $t('massCareImportingNotice') }}</span>
        </div>
        <div class="mb-3" v-if="totalRows">
          <b-progress :max="totalRows">
            <b-progress-bar :value="successCount+errorCount" :label="`${(((successCount + errorCount) / totalRows) * 100).toFixed(0)}%`"></b-progress-bar>
          </b-progress>
          <div class="text-center">{{ successCount + errorCount }}/{{ totalRows }} | {{ $t('error') }}: {{ errorCount }}
            <a @click="downloadErrors" class="btn-link text-primary" href="javascript:">[{{ $t('detail') }}]</a>
          </div>
        </div>
        <template v-slot:modal-footer>
          <button class="btn btn-secondary" @click="cancel" v-if="!reading&&importing">{{ $t('cancel') }}</button>
          <button class="btn btn-primary" @click="reset" v-if="!reading&&!importing&&totalRows>0">Reset</button>
          <button v-if="totalRows===0" class="btn btn-primary" @click="process" :disabled="reading||importing">
            <b-spinner small v-if="reading||importing"></b-spinner>
            {{ $t('assign') }}
          </button>
        </template>
      </b-modal>
    </button>
</template>

<script>
import fileReaderStream from 'filereader-stream';
import {saveAs} from 'file-saver'
import Excel from 'exceljs';
import AdminUserSelect from '@/components/AdminUserSelect';

export default {
  components: {AdminUserSelect },
  data() {
    return {
      show: false,
      file: null,
      reading: false,
      importing: false,
      totalRows: 0,
      successCount: 0,
      errorCount: 0,
      importErrors: [],
      cancelRequest: false,
      formModel: {
        account: null
      },
      formErrors: [],
      fileError: null,
    }
  },
  methods: {
    close(evt) {
      if (this.reading || this.importing) {
        evt.preventDefault();
      } else {
        this.reset();
      }
    },
    reset() {
      this.totalRows = 0;
      this.successCount = 0;
      this.errorCount = 0;
      this.importErrors = [];
      this.cancelRequest = false;
      this.reading = false;
      this.importing = false;
      this.file = null;
      this.fileError = null;
      this.formModel.account = null;
    },
    process(evt) {
      evt.preventDefault();
      this.fileError = null;
      this.formErrors = [];
      if (!this.file) {
        this.fileError = this.$t('notEmpty');
      } else {
        if (!(this.file.name.endsWith('.xls') || this.file.name.endsWith('.xlsx'))) {
          this.fileError = this.$t('invalidFile');
        }
      }
      if (!this.formModel.account) {
        this.formErrors = [{msg: this.$t('notEmpty'), param: 'account'}];
      }
      if (this.fileError || this.formErrors.length) {
        return;
      }
      //
      if (this.file.name.endsWith('.xls') || this.file.name.endsWith('.xlsx')) {
        this.reading = true;
        let readStream = fileReaderStream(this.file);
        const workbook = new Excel.Workbook();
        workbook.xlsx.read(readStream).then(async () => {
          if (workbook.worksheets.length === 0) {
            this.fileError = this.$t('invalidFile');
            return;
          }
          let sheet = workbook.worksheets[0];
          if (sheet.actualRowCount < 2) {
            this.fileError = this.$t('emptyFile');
            return;
          }
          if (!sheet.getRow(1).values.find(v => v && String(v).trim() === 'domain')) {
            this.fileError = this.$t('invalidFile');
            return;
          }

          let headerRow = sheet.getRow(1);
          this.totalRows = sheet.actualRowCount - 1;
          this.reading = false;
          this.importing = true;
          let i = 2;
          let data = [];
          while (i <= sheet.actualRowCount) {
            if (this.cancelRequest) {
              break;
            }
            let item = this.getItem(headerRow, sheet.getRow(i));
            item.row = i;
            data.push(item);
            i+= 1;
          }
          let response = await this.$service.post('/client/mass-care', {...this.formModel, data: data});
          if (response) {
            this.successCount = response.data.successCount;
            this.errorCount = response.data.inputErrors.length;
            this.importErrors = response.data.inputErrors;
            this.$bus.$emit('loadClient');
          }
        }).catch(err => {
          this.$swal({
            title: this.$t('error'),
            text: err.message,
            icon: 'error',
          });
        }).finally(() => {
          this.reading = false;
          this.importing = false;
          this.cancelRequest = false;
          this.$bus.$emit('reloadCustomerList');
        });
      }
    },
    cancel() {
      this.cancelRequest = true;
    },
    downloadErrors() {
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('Import errors');
      sheet.columns = [
        {header: this.$t('row'), key: 'row', width: 15},
        {header: this.$t('error'), key: 'error', width: 50},
      ];
      for (let err of this.importErrors) {
        sheet.addRow({
          row: err.row,
          error: err.msg
        });
      }
      workbook.xlsx.writeBuffer({base64: true}).then(data => {
        saveAs(
            new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'mass-care-error.xlsx'
        )
      });
    },
    downloadTemplate: function () {
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('Clients');
      sheet.columns = [
        {header: 'domain', key: 'domain', width: 25},
      ]
      workbook.xlsx.writeBuffer({base64: true}).then(data => {
        saveAs(
            new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'mass-care-template.xlsx'
        )
      });
    },
    getItem(headerRow, row) {
      let item = {};
      for (let j = 1; j <= headerRow.cellCount; j++) {
        let key = headerRow.getCell(j).value;
        let value;
        if (row.getCell(j).isHyperlink) {
          value = row.getCell(j).value.text;
        } else {
          value = row.getCell(j).value;
        }
        if (typeof value === 'string') {
          value = value.trim();
        }
        item[key] = value;
      }
      return item;
    },
  },
  i18n: {
    messages: {
      en: {
        title: "Mass care",
        downloadTemplate: "Download template",
        selectFile: "Select file",
        selectAccount: "Select CS",
        assign: "Assign",
        notEmpty: "Required information",
        invalidFile: "Invalid file",
        emptyFile: "File has no data",
        invalidData: "{0}/{1} invalid row",
        viewDetail: "View details",
        formFilePlaceholder: "Choose a file or drop it here...",
        changeConfirm: "Are you sue to do this action?",
        account: 'Account',
        row: 'Row',
        massCareImportingNotice: 'Uploading data'
      },
      vi: {
        title: "Phân công chăm sóc",
        downloadTemplate: "Tải xuống file mẫu",
        selectFile: "Chọn file",
        selectAccount: "Chọn NV chăm sóc",
        assign: "Phân công",
        notEmpty: "Thông tin bắt buộc",
        invalidFile: "File không hợp lệ",
        emptyFile: "File không có dữ liệu",
        invalidData: "{0}/{1} dòng không hợp lệ",
        viewDetail: "Xem chi tiết",
        formFilePlaceholder: "Chọn file hoặc kéo thả vào đây",
        changeConfirm: "Bạn có chắc chắn muốn thực hiện hành động này?",
        account: 'NV chăm sóc',
        row: 'Dòng',
        massCareImportingNotice: 'Đang tải lên dữ liệu'
      }
    }
  }
}
</script>
